import LegalDocumentLayout from "./LegalDocumentLayout";
import LegalPageWithOverride from "./LegalPageWithOverride";
import SeoHead from "@/components/SeoHead";

export default function CompliancePage() {
  return (
    <>
      <SeoHead route="/compliance" />
      <LegalPageWithOverride
        slug="compliance"
        title="Compliance & Data Practices"
        lastUpdatedFallback="October 7, 2026"
      >
      <LegalDocumentLayout title="Compliance & Data Practices" lastUpdated="October 7, 2026">
      <p>
        Acquigine takes compliance seriously. This page describes what we do ,  not marketing language, but the actual controls in place. This page is provided for transparency and does not constitute legal advice.
      </p>

      <h2>1. Data Sourcing Policy</h2>
      <ul>
        <li><strong>Licensed data providers only.</strong> Every third-party data provider is used under a paid or platform-attributed license per that provider's Terms of Service.</li>
        <li><strong>Public records.</strong> We ingest public records and registries maintained by government agencies, including business filings, tax lien filings, and municipal code-enforcement data. Additional public and licensed sources are evaluated on an ongoing basis and added once ingest is live.</li>
        <li><strong>No scraping.</strong> We do not scrape sites that prohibit scraping. When a public dataset requires an API or SFTP handshake, we authenticate and pull under the terms published by the data owner.</li>
      </ul>

      <h2>2. Outreach Compliance</h2>
      <ul>
        <li><strong>CAN-SPAM.</strong> Every outbound email sent via Acquigine includes the sender's physical postal address, a clear advertisement disclosure, and a working one-click unsubscribe link. We refuse to render a sequence that lacks either.</li>
        <li><strong>Opt-out enforcement.</strong> Every unsubscribe (via the in-body link or the sender's own provider dashboard) is added to a global suppression list and enforced platform-wide within 10 business days. We refuse to send to any suppressed address.</li>
        <li><strong>Suppression lists.</strong> Each subscriber maintains a suppression list; contacts on that list are removed from every downstream sequence and campaign.</li>
        <li><strong>Auto-outreach placeholder guard.</strong> Records lacking a real entity name or owner identity (for example, unresolved Florida dissolution stubs) are blocked from every outreach path.</li>
      </ul>

      <h2>3. FCRA Notice</h2>
      <p>
        <strong>The data Acquigine provides is not a consumer report.</strong> It may not be used, in whole or in part, to determine an individual's eligibility for:
      </p>
      <ul>
        <li>Credit or insurance for personal, family, or household purposes</li>
        <li>Employment</li>
        <li>Housing (as a tenant)</li>
        <li>Any other purpose covered by the Fair Credit Reporting Act (FCRA)</li>
      </ul>
      <p>
        Acquigine is a commercial B2B intelligence tool designed to help acquirers identify and evaluate potential acquisition opportunities. When you first attempt to send outreach or generate a document, you will be asked to acknowledge this limitation in-app.
      </p>

      <h2>4. Fair Housing</h2>
      <p>
        The Acquigine scoring engine <strong>refuses to compute a score</strong> when a protected-class proxy variable is detected in the opportunity record (race, national origin, familial status, disability, and other categories under the Fair Housing Act). The record is returned with <code>compliance_check.passed = false</code> and the score is null. This safeguard is code-level and cannot be bypassed by tier.
      </p>

      <h2>5. Payment Security</h2>
      <p>
        All card data is collected and processed by our payment processing provider, a PCI DSS Level 1 certified processor. Acquigine servers never receive raw card numbers, CVCs, or expiration dates. We store only the customer and subscription IDs necessary to manage your account.
      </p>

      <h2>6. Data at Rest and in Transit</h2>
      <ul>
        <li>TLS 1.2+ on all API endpoints; HSTS enforced on the public domain.</li>
        <li>Passwords bcrypt-hashed with per-user salt.</li>
        <li>Subscriber-provided API keys for connected services are encrypted at rest.</li>
        <li>JWT-based session tokens with short expiry and rotation on password change.</li>
        <li>Role-based access; admin actions written to an immutable audit log.</li>
      </ul>

      <h2>7. Third-Party Communication</h2>
      <p>
        When you connect an outbound email or e-signature provider, communications are sent from <strong>your</strong> account under <strong>your</strong> credentials. You are the sender under CAN-SPAM. Acquigine is the tooling that helped you compose and dispatch the message; the compliance obligations of the sender remain with you.
      </p>

      <h2>8. Reporting Issues</h2>
      <p>
        If you believe our platform has been used to send you unwanted communication, or if you have a compliance concern about a data source or outreach path,
        please email <a href="mailto:compliance@acquigine.com">compliance@acquigine.com</a>. We investigate every report and, where appropriate, add the sending user to internal review.
      </p>

      <h2>9. Retention and Deletion</h2>
      <p>
        Data-deletion requests are handled through <a href="mailto:privacy@acquigine.com">privacy@acquigine.com</a>. See our <a href="/privacy">Privacy Policy</a> for full retention terms.
      </p>

      <h2>10. Attorney Review</h2>
      <p>
        The document templates in Acquigine ,  LOIs, purchase and sale agreements, marketing letters, negotiation memos ,  are informational starting points. State-specific templates (FL, TX, CA, NY, GA, AZ, NC, OH, IL, PA) approximate typical practice, and all other states are covered by our Standard template with governing-law, deed-type, and disclosure fields automatically inserted. <strong>Attorney review is strongly recommended</strong> before any document is executed.
      </p>
    </LegalDocumentLayout>
      </LegalPageWithOverride>
    </>
  );
}
